import { useMemo, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTreatmentSupportRequests } from '@/hooks/use-treatment-support';
import { useLinks } from '@/lib/firebase/queries/links';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { Loader2, Search, User, Heart, HandCoins } from 'lucide-react';
import type { TreatmentSupportRequest, TreatmentSupportStatus } from '@/types';

// Copied from web/src/components/dashboard/CoordinatorDashboard.tsx L53-63 (off-limits to
// import from — it's an unexported local const in that file). M5 should reconcile this copy
// with the original (and with mobile's separate copy in
// mobile/src/screens/admin/CaseDetailScreen.tsx L116-126) into one shared source.
const STATUS_BADGE_CLASS: Record<TreatmentSupportStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-orange-100 text-orange-800',
  pending_approval: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
  approved_treatment: 'bg-teal-100 text-teal-800',
  treatment_started: 'bg-indigo-100 text-indigo-800',
  rejected: 'bg-red-100 text-red-800',
  pending_invoices: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

// Copied from web/src/components/dashboard/CoordinatorDashboard.tsx L212-217
// (`getNetMedicalCost`, unexported local closure — off-limits file). M5 should reconcile
// this copy with the original and with mobile's port in
// mobile/src/screens/admin/caseAllocation.ts (getCaseRemainingBudget's inline invoice total).
function getNetMedicalCost(req: Pick<TreatmentSupportRequest, 'cost' | 'invoices'>): number {
  const invoiceTotal = Array.isArray(req.invoices)
    ? req.invoices.reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0)
    : 0;
  return invoiceTotal > 0 ? invoiceTotal : (req.cost || 0);
}

// Copied from web/src/components/dashboard/CoordinatorDashboard.tsx L1468-1486
// (`getCaseRemainingBudget`, unexported local closure — off-limits file), adapted to take the
// case object directly instead of looking it up by id from a closed-over `requests` array.
// M5 should reconcile this copy with the original and with mobile's near-identical port in
// mobile/src/screens/admin/caseAllocation.ts (getCaseRemainingBudget, cited there as "Web
// L2303-2328" against AdminHelpManagement.tsx — a THIRD independent copy of this same math).
function getCaseRemainingBudget(
  req: Pick<TreatmentSupportRequest, 'cost' | 'invoices' | 'donationEntries'>
): { caseCost: number; totalFunded: number; remaining: number } {
  const invoicesTotal = req.invoices && req.invoices.length > 0
    ? req.invoices.reduce((sum, inv) => sum + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0)
    : 0;
  const hasInvoices = invoicesTotal > 0;
  const caseCost = hasInvoices ? invoicesTotal : (req.cost || 0);
  const totalFunded = (req.donationEntries || []).reduce((sum, e) => sum + (e.amount || 0), 0);
  return { caseCost, totalFunded, remaining: caseCost - totalFunded };
}

// Same rounding rule as web/src/components/dashboard/CoordinatorDashboard.tsx L4819
// (coveragePct is computed inline there too, not a named local — no separate citation needed).
function coveragePct(caseCost: number, totalFunded: number): number {
  return caseCost > 0 ? Math.round((totalFunded / caseCost) * 100) : 0;
}

type StatusFilter = TreatmentSupportStatus | 'all';

const STATUS_ORDER: TreatmentSupportStatus[] = [
  'pending',
  'under_review',
  'pending_approval',
  'approved',
  'approved_treatment',
  'treatment_started',
  'pending_invoices',
  'completed',
  'rejected',
];

function displayName(c: Pick<TreatmentSupportRequest, 'nameArabic' | 'nameEnglish'>, rtl: boolean): string {
  return rtl ? (c.nameArabic || c.nameEnglish || '—') : (c.nameEnglish || c.nameArabic || '—');
}

/**
 * Read-only cases view for the marketer role (M4). Deliberately NOT a reproduction of
 * CoordinatorDashboard.tsx's cases surface (renderRequestsTable + the ~1,815-line detail
 * dialog) — see docs/superpowers/audits/2026-07-27-web-cases-readonly-contract.md §5: that
 * would be 1,200-1,450 new lines for a role that can only look. Instead this mirrors what
 * survives marketer gating on mobile (CaseDetailScreen.tsx, gated via marketerCaseAccess.ts's
 * empty allowlist): a case list with search + status filter, and a detail view of beneficiary
 * identity, status, cost/coverage, and per-project funding. No mutating control of any kind —
 * not even a disabled one — is rendered anywhere in this file.
 *
 * Data is read-only: `useTreatmentSupportRequests` fetches once on mount via a plain
 * `getDocs()` (web/src/hooks/use-treatment-support.ts, web/src/lib/firebase/queries/
 * treatmentSupport.ts:55-56) and `useLinks` subscribes read-only via `onSnapshot`
 * (web/src/lib/firebase/queries/links.ts:36-41, 72-85). Neither hook is given its
 * add/update/delete members here — this component only destructures `requests`/`loading`
 * and `links`. Opening the list or a case's detail dialog only sets local component state
 * (searchQuery/statusFilter/selectedCaseId) — no Firestore write occurs on mount or on
 * opening a case, matching the audit's §4 finding for the coordinator's own detail dialog.
 */
export function MarketerCases() {
  const { t, dir } = useLanguage();
  const rtl = dir === 'rtl';
  const { requests, loading } = useTreatmentSupportRequests();
  const { links } = useLinks();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  const linkById = useMemo(() => {
    const m = new Map<string, { linkTitle?: string }>();
    for (const l of links) m.set(l.id, { linkTitle: l.linkTitle });
    return m;
  }, [links]);

  const filtered = useMemo(() => {
    let result = requests;
    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((r) =>
        (r.nameArabic || '').toLowerCase().includes(q) ||
        (r.nameEnglish || '').toLowerCase().includes(q) ||
        (r.idNumber || '').toLowerCase().includes(q) ||
        (r.medicalCondition || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [requests, statusFilter, searchQuery]);

  const selectedCase = useMemo(
    () => (selectedCaseId ? requests.find((r) => r.id === selectedCaseId) || null : null),
    [requests, selectedCaseId]
  );

  const headClass = rtl ? 'text-right' : 'text-left';

  return (
    <div className="space-y-4" dir={dir}>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.marketer.cases.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ps-10"
          />
        </div>
        <div className="w-full sm:w-64">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('dashboard.marketer.cases.statusAll')}</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`dashboard.coordinator.statuses.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground whitespace-nowrap">
          <span className="font-semibold text-foreground">{filtered.length.toLocaleString('en-US')}</span>
          {t('dashboard.marketer.cases.totalCases')}
        </span>
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t('dashboard.marketer.cases.title')}</h3>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin me-2" />
              {t('dashboard.marketer.cases.loading')}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('dashboard.marketer.cases.noCases')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className={headClass}>{t('dashboard.marketer.cases.columns.caseNumber')}</TableHead>
                    <TableHead className={headClass}>{t('dashboard.marketer.cases.columns.name')}</TableHead>
                    <TableHead className={headClass}>{t('dashboard.marketer.cases.columns.status')}</TableHead>
                    <TableHead className={headClass}>{t('dashboard.marketer.cases.columns.cost')}</TableHead>
                    <TableHead className={headClass}>{t('dashboard.marketer.cases.columns.funded')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => {
                    const cost = getNetMedicalCost(c);
                    const funded = (c.donationEntries || []).reduce((s, e) => s + (e.amount || 0), 0);
                    return (
                      <TableRow
                        key={c.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedCaseId(c.id)}
                      >
                        <TableCell className="font-mono text-xs" dir="ltr">#{c.caseNumber ?? '—'}</TableCell>
                        <TableCell className="font-medium">{displayName(c, rtl)}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGE_CLASS[c.status]} variant="outline">
                            {t(`dashboard.coordinator.statuses.${c.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1" dir="ltr">
                            {Math.round(cost).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1" dir="ltr">
                            {Math.round(funded).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3 w-3" />
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedCase} onOpenChange={(open) => { if (!open) setSelectedCaseId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedCase && (() => {
            const budget = getCaseRemainingBudget(selectedCase);
            const coverage = coveragePct(budget.caseCost, budget.totalFunded);
            const entries = selectedCase.donationEntries || [];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 flex-wrap">
                    <span>{displayName(selectedCase, rtl)}</span>
                    <Badge className={STATUS_BADGE_CLASS[selectedCase.status]} variant="outline">
                      {t(`dashboard.coordinator.statuses.${selectedCase.status}`)}
                    </Badge>
                  </DialogTitle>
                  <DialogDescription dir="ltr">#{selectedCase.caseNumber ?? '—'}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                  {/* Beneficiary identity */}
                  <div className="space-y-2">
                    <h4 className="flex items-center gap-2 font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      <User className="h-4 w-4" /> {t('dashboard.marketer.cases.detail.identity')}
                    </h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <span className="text-muted-foreground">{t('dashboard.marketer.cases.detail.name')}</span>
                      <span className="font-medium">{displayName(selectedCase, rtl)}</span>
                      <span className="text-muted-foreground">{t('dashboard.marketer.cases.detail.nationalId')}</span>
                      <span className="font-mono" dir="ltr">{selectedCase.idNumber || '—'}</span>
                    </div>
                  </div>

                  {/* Cost & coverage */}
                  <div className="space-y-2">
                    <h4 className="flex items-center gap-2 font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      <Heart className="h-4 w-4" /> {t('dashboard.marketer.cases.detail.costCoverage')}
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-amber-50 border border-amber-200">
                        <span className="text-sm text-amber-700">{t('dashboard.coordinator.caseCost')}</span>
                        <span className="font-semibold text-amber-900 inline-flex items-center gap-1" dir="ltr">
                          {Math.round(budget.caseCost).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-blue-50 border border-blue-200">
                        {/* M4 fix: was t('dashboard.coordinator.totalFunded') ("Allocated"), which
                            disagreed with the table's own "Funded" column for the identical figure.
                            Uses a marketer-owned key instead of editing the shared coordinator key,
                            so CoordinatorDashboard.tsx's rendering is untouched. */}
                        <span className="text-sm text-blue-700">{t('dashboard.marketer.cases.detail.funded')}</span>
                        <span className="font-semibold text-blue-900 inline-flex items-center gap-1" dir="ltr">
                          {Math.round(budget.totalFunded).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-[10rem] flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border border-emerald-200">
                        <span className="text-sm text-emerald-700">{t('dashboard.coordinator.coverage')}</span>
                        <span className="font-semibold text-emerald-900">{coverage}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Project funding */}
                  <div className="space-y-2">
                    <h4 className="flex items-center gap-2 font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      <HandCoins className="h-4 w-4" /> {t('dashboard.marketer.cases.detail.fundingSources')}
                    </h4>
                    {entries.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        {t('dashboard.marketer.cases.detail.noFunding')}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {entries.map((entry, idx) => {
                          const linkTitle = (entry.linkId && linkById.get(entry.linkId)?.linkTitle) || entry.linkTitle;
                          return (
                            <div key={idx} className="flex items-center justify-between gap-3 p-3 border rounded-lg bg-card">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary">{t(`dashboard.coordinator.platforms.${entry.platform}`)}</Badge>
                                <span className="font-mono text-sm" dir="ltr">{entry.caseNumber}</span>
                                {linkTitle && <span className="text-sm text-muted-foreground">{linkTitle}</span>}
                              </div>
                              <span className="font-medium inline-flex items-center gap-1" dir="ltr">
                                {Math.round(entry.amount || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
